const MAPKIT_SRC = "https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js";

// Uses the inline token (from data-map-token-value) for initial auth,
// falls back to fetching /mapkit_token for renewal past the 1-hour expiry.
function loadAppleMaps(token) {
  return new Promise((resolve, reject) => {
    if (window.mapkit) {
      resolve(window.mapkit);
      return;
    }

    const script = document.createElement("script");
    script.src = MAPKIT_SRC;
    script.async = true;

    let initialToken = token;
    script.onload = () => {
      mapkit.init({
        authorizationCallback: async (done) => {
          if (initialToken) {
            done(initialToken);
            initialToken = null;
          } else {
            const response = await fetch("/mapkit_token");
            const data = await response.json();
            done(data.token);
          }
        },
      });
      resolve(window.mapkit);
    };
    script.onerror = reject;

    document.head.appendChild(script);
  });
}

// === Geocoding ===
//
// Pages that geocode (searchbox, quiz, location pickers) often render no map
// at all, so they need MapKit loaded *and authorized* before a Geocoder can be
// built — a different entry point from loadAppleMaps above, which serves a map
// container that already carries an inline token.

let geocoderReady = null;
let geocoder = null;

// Loads and authorizes MapKit for service use. Kept separate from
// loadAppleMaps because there is no inline token on a map-less page.
async function loadMapKitForServices() {
  if (typeof mapkit !== "undefined" && typeof mapkit.Geocoder === "function") {
    return;
  }

  await new Promise((resolve, reject) => {
    // Another controller may have already injected the script; wait it out
    // rather than adding a second copy.
    if (document.querySelector('script[src*="mapkit"]')) {
      const check = setInterval(() => {
        if (typeof mapkit !== "undefined") {
          clearInterval(check);
          resolve();
        }
      }, 100);
      setTimeout(() => {
        clearInterval(check);
        reject(new Error("MapKit load timeout"));
      }, 10000);
      return;
    }

    const script = document.createElement("script");
    script.src = MAPKIT_SRC;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  if (!mapkit.services) {
    await new Promise((resolve, reject) => {
      mapkit.init({
        authorizationCallback: async (done) => {
          try {
            const response = await fetch("/mapkit_token");
            const data = await response.json();
            done(data.token);
            resolve();
          } catch (error) {
            console.error("Failed to fetch MapKit token:", error);
            reject(error);
          }
        },
      });
    });
  }
}

// Builds the page's shared Geocoder, loading MapKit first. Idempotent, and
// safe to call before the user has typed anything — callers that know a lookup
// is coming (the quiz intro) call this early so the first lookup is instant.
// The in-flight promise is cached so concurrent callers share one load; a
// failed load is uncached so the next caller retries, since the eager preload
// must not poison a later user-initiated lookup.
export function ensureGeocoder() {
  if (!geocoderReady) {
    geocoderReady = loadMapKitForServices().then(
      () => {
        geocoder = new mapkit.Geocoder({ language: "en" });
      },
      (error) => {
        geocoderReady = null;
        throw error;
      },
    );
  }
  return geocoderReady;
}

// Resolves a free-text address to { lat, lng, displayName }.
//
// `biasCoordinate` is an optional { latitude, longitude } that nudges ambiguous
// queries toward a known point (the quiz passes the visitor's Cloudflare IP
// geo hint). Callers without a hint pass nothing.
export async function geocodeUsAddress(address, biasCoordinate) {
  await ensureGeocoder();

  // US-only directory; without this, zip "31407" (Savannah, GA) geocodes
  // to a Singapore postal district and the user gets zero results.
  const options = { limitToCountries: "US" };
  if (biasCoordinate?.latitude && biasCoordinate?.longitude) {
    options.coordinate = new mapkit.Coordinate(
      biasCoordinate.latitude,
      biasCoordinate.longitude,
    );
  }

  return new Promise((resolve, reject) => {
    geocoder.lookup(
      address,
      (error, data) => {
        if (error || !data.results?.length) {
          reject(error || new Error("No results"));
          return;
        }
        const r = data.results[0];
        resolve({
          lat: r.coordinate.latitude,
          lng: r.coordinate.longitude,
          displayName: r.formattedAddress || address,
        });
      },
      options,
    );
  });
}

export default class AppleMapService {
  constructor(containerId, options) {
    this.containerId = containerId;
    this.options = options;
  }

  async init(token) {
    await loadAppleMaps(token);

    // Convert options to MapKit format
    const mapOptions = {};

    if (this.options.center?.lat && this.options.center?.lng) {
      mapOptions.center = new mapkit.Coordinate(
        this.options.center.lat,
        this.options.center.lng,
      );
    }

    // MapKit uses a different zoom concept - we approximate with a span
    // Zoom level 11 ≈ 0.1 degree span, higher zoom = smaller span
    if (this.options.zoom) {
      const span = 0.5 / Math.pow(2, this.options.zoom - 10);
      mapOptions.region = new mapkit.CoordinateRegion(
        mapOptions.center || new mapkit.Coordinate(40.7128, -74.006),
        new mapkit.CoordinateSpan(span, span),
      );
    }

    this.map = new mapkit.Map(
      document.getElementById(this.containerId),
      mapOptions,
    );

    // Track user gestures to distinguish user-initiated vs programmatic map moves
    this.lastUserGestureTime = 0;
    this.hasOpenCallout = false;

    const mapContainer = document.getElementById(this.containerId);
    ["touchstart", "mousedown", "wheel"].forEach((type) => {
      mapContainer.addEventListener(
        type,
        () => {
          this.lastUserGestureTime = Date.now();
        },
        { passive: true },
      );
    });

    // Track callout state - marker clicks ARE user gestures, but shouldn't trigger auto-search
    this.map.addEventListener("select", () => {
      this.hasOpenCallout = true;
    });
    this.map.addEventListener("deselect", () => {
      this.hasOpenCallout = false;
    });
  }

  addMarker(lat, lng, label, content, calloutData = {}) {
    const map = this.map;
    const containerId = this.containerId;
    const coordinate = new mapkit.Coordinate(lat, lng);

    // Get display text: use content's text (number) if available, else label
    const displayText = content?.textContent || label;

    // Generate unique ID for this marker
    const markerId = `marker-${displayText}`;

    // Store reference to element for highlighting
    let markerElement = null;

    // Use custom Annotation with DOM element for hover support
    const annotation = new mapkit.Annotation(
      coordinate,
      (coord) => {
        const el = document.createElement("div");
        el.className = "apple-marker";
        el.dataset.markerId = markerId;
        el.innerHTML = `
          <div class="apple-marker__pin">
            <span class="apple-marker__label"></span>
          </div>
        `;
        el.querySelector(".apple-marker__label").textContent = displayText;
        markerElement = el; // Store reference
        return el;
      },
      {
        title: label,
        callout: this.createCalloutDelegate(calloutData, label),
      },
    );

    this.map.addAnnotation(annotation);

    // Helper to find element (handles lazy rendering)
    const getElement = () => {
      if (markerElement) return markerElement;
      // Fall back to DOM query if element reference lost
      const container = document.getElementById(containerId);
      return container?.querySelector(`[data-marker-id="${markerId}"]`);
    };

    // Return a wrapper that mimics Google Maps marker API for compatibility
    return {
      annotation,
      get content() {
        return getElement();
      },
      zIndex: 0,
      addListener(event, callback) {
        if (event === "click") {
          annotation.addEventListener("select", callback);
        }
      },
      setMap(m) {
        if (m === null) {
          map.removeAnnotation(annotation);
        }
      },
      highlight() {
        const el = getElement();
        const pin = el?.querySelector(".apple-marker__pin");
        if (pin) {
          pin.classList.add("apple-marker__pin--highlight");
        }
      },
      unhighlight() {
        const el = getElement();
        const pin = el?.querySelector(".apple-marker__pin");
        if (pin) {
          pin.classList.remove("apple-marker__pin--highlight");
        }
      },
    };
  }

  setCenter(lat, lng) {
    const newCenter = new mapkit.Coordinate(lat, lng);
    this.map.setCenterAnimated(newCenter, true);
  }

  // Enable/disable scroll and zoom interaction (for mobile bottom sheet)
  setInteraction(enabled) {
    this.map.isScrollEnabled = enabled;
    this.map.isZoomEnabled = enabled;
  }

  // Clean up MapKit instance
  destroy() {
    if (this.map) {
      this.map.destroy();
      this.map = null;
    }
  }

  removeMarker(marker) {
    // marker is a wrapper object with .annotation property
    const annotation = marker.annotation || marker;
    this.map.removeAnnotation(annotation);
  }

  // Standard MapKit marker (native pin) - for simple single-location maps
  addSimpleMarker(lat, lng, title) {
    const coordinate = new mapkit.Coordinate(lat, lng);
    const marker = new mapkit.MarkerAnnotation(coordinate, {
      title: title,
      color: "#EA4335", // Match our brand color
    });
    this.map.addAnnotation(marker);
    return marker;
  }

  onDragStart(callback) {
    this.map.addEventListener("region-change-start", () => {
      // Only respond if there was a recent user gesture
      const wasUserInitiated = Date.now() - this.lastUserGestureTime < 2000;
      if (!wasUserInitiated) return;
      callback();
    });
  }

  onDragEnd(callback) {
    this.map.addEventListener("region-change-end", () => {
      // Only respond to actual map drags, not programmatic moves or callout pans
      // 1. Must have recent user gesture (blocks setCenter, showAnnotations, etc.)
      // 2. Must not have open callout (blocks pan-to-show-callout after marker click)
      const wasUserInitiated = Date.now() - this.lastUserGestureTime < 2000;
      if (!wasUserInitiated || this.hasOpenCallout) return;

      callback({
        center: this.getCenter(),
        bounds: this.getBounds(),
      });
    });
  }

  getCenter() {
    const center = this.map.center;
    return { lat: center.latitude, lng: center.longitude };
  }

  getBounds() {
    const region = this.map.region;
    return {
      north: region.center.latitude + region.span.latitudeDelta / 2,
      south: region.center.latitude - region.span.latitudeDelta / 2,
      east: region.center.longitude + region.span.longitudeDelta / 2,
      west: region.center.longitude - region.span.longitudeDelta / 2,
    };
  }

  // Animate map to fit all current annotations with padding
  showAnnotations(padding = { top: 60, right: 40, bottom: 40, left: 40 }) {
    const annotations = this.map.annotations;
    if (annotations.length === 0) return;

    this.map.showItems(annotations, {
      animate: true,
      padding: new mapkit.Padding(padding),
    });
  }

  createCalloutDelegate(data, name) {
    const { url, image, denomination, location } = data;

    return {
      calloutElementForAnnotation: () => {
        const el = document.createElement("div");
        el.className = "apple-callout";
        el.innerHTML = `
          <a href="#" target="_blank" rel="noopener noreferrer" class="apple-callout__link">
            <img src="" alt="" class="apple-callout__image" style="display:none" />
            <div class="apple-callout__content">
              <div class="apple-callout__name"></div>
              <div class="apple-callout__details">
                <span class="apple-callout__denomination"></span>
                <span class="apple-callout__separator"></span>
                <span class="apple-callout__location"></span>
              </div>
            </div>
          </a>
        `;

        const link = el.querySelector(".apple-callout__link");
        if (url) link.href = url;

        const img = el.querySelector(".apple-callout__image");
        if (image) {
          img.src = image;
          img.style.display = "";
        }

        el.querySelector(".apple-callout__name").textContent = name;

        const denomEl = el.querySelector(".apple-callout__denomination");
        if (denomination) denomEl.textContent = denomination;

        const sepEl = el.querySelector(".apple-callout__separator");
        if (denomination && location) sepEl.textContent = " · ";

        const locEl = el.querySelector(".apple-callout__location");
        if (location) locEl.textContent = location;

        return el;
      },
    };
  }
}
